import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { containsTarget } from '../../lib/DomUtils/utils';
import { GlobalMenuState, useGlobalMenu } from '../../contexts/GlobalMenuStore';
import { KeysOfOpenPanelsType, MainNavigationProps, NavigationStateType } from './MainNavigationTypes';
import { KeyEvent } from '../../types/KeyEvent';
import { GlobalDocumentIds } from '../../types/GlobalDocumentIds';
import { ModalDialogBodyNoScrolling } from '../../components/organisms/ModalDialog/ModalDialog';

const getInitialState = (props: MainNavigationProps): NavigationStateType => {
  return new Map([
    [
      'mainNavigation',
      {
        panelData: props,
        panelName: undefined,
        panelLink: undefined,
        panelLevel: 0,
        loadingError: false,
        fetchUrl: undefined,
        nextPanelButtonRef: undefined,
      },
    ],
  ]);
};

export function useMainNavigation(props: MainNavigationProps) {
  const { closeMenus, currentOpenMenu } = useGlobalMenu();

  const mapRef = useRef<NavigationStateType>(getInitialState(props));
  const [keysOfOpenPanels, setKeysOfOpenPanels] = useState<KeysOfOpenPanelsType>(['mainNavigation']);
  const [subPanelContainerClass, setSubPanelContainerClass] = useState<string>('main-navigation__sub-panels');
  const [activeOpenButtonId, setActiveOpenButtonId] = useState<string | undefined>(undefined);
  const navRef = useRef<HTMLElement>(null);
  const subPanelsContainerRef = useRef<HTMLDivElement>(null);
  const mainNavigationStartingPoint = useRef<string>(undefined);

  /**
   * If sub panels open, content will be hidden (attribute 'inert'), body will be not scrollable.
   */
  const hideBackgroundContent = () => {
    const mainContent = document.getElementById(GlobalDocumentIds.mainContentWrapper);
    const palContainer = document.getElementById(GlobalDocumentIds.palContainer);
    document.body.classList.add(ModalDialogBodyNoScrolling);
    mainContent?.setAttribute('inert', 'true');
    palContainer?.setAttribute('inert', 'true');
  };

  /**
   * If there is no sub panels open, content will be interactive again
   */
  const showBackgroundContent = () => {
    const mainContent = document.getElementById(GlobalDocumentIds.mainContentWrapper);
    const palContainer = document.getElementById(GlobalDocumentIds.palContainer);
    document.body.classList.remove(ModalDialogBodyNoScrolling);
    mainContent?.removeAttribute('inert');
    palContainer?.removeAttribute('inert');
  };

  /**
   * Hiding or showing content depends on count of open sub panels. Be aware first panel is main panel.
   * Changing classes on container panel for CSS transitions (max 2 sub panels ar visible).
   * If all sub panels are closed, activeOpenButtonId of this panel will be set to undefined,
   * so it is possible to close NextPanelButton without losing focus.
   */

  useEffect(() => {
    if (mainNavigationStartingPoint.current && currentOpenMenu === GlobalMenuState.MAIN_NAVIGATION) {
      document.getElementById(mainNavigationStartingPoint.current)?.focus();
      mainNavigationStartingPoint.current = undefined;
    }

    if (activeOpenButtonId && keysOfOpenPanels.length === 1) {
      mainNavigationStartingPoint.current = activeOpenButtonId;
      setActiveOpenButtonId(undefined);
      showBackgroundContent();
      setSubPanelContainerClass('main-navigation__sub-panels');
    }

    if (keysOfOpenPanels.length > 1) {
      const level = keysOfOpenPanels.length >= 4 ? 3 : keysOfOpenPanels.length - 1;
      hideBackgroundContent();
      setSubPanelContainerClass(`main-navigation__sub-panels main-navigation__sub-panels--subpanel${level}-visible`);
    }
  }, [keysOfOpenPanels, activeOpenButtonId]);

  /**
   * Showing content depends on count of open sub panels. Be aware first panel is main panel.
   */
  useEffect(() => {
    return () => {
      showBackgroundContent();
    };
  }, []);

  const closeAllSubPanels = useCallback(() => {
    mapRef.current = getInitialState(props);
    setKeysOfOpenPanels(['mainNavigation']);
  }, [props]);

  /**
   * Close the nav when the focus leaves it.
   * */
  const closeMainNavigationWhenFocusOut = (event: React.FocusEvent) => {
    const nextElement = event.relatedTarget;
    // We need to check if our next target is the Main Menu button.
    // Otherwise, after navigating to submenus, this will trigger closeMenus() ADDITIONALLY to the button onClick(),
    // which leads to the Menu not closing correctly on button click due to a state mismatch in GlobalMenuStore.
    const isMenuButton = nextElement?.classList.contains('header-collapsible--navigation');
    if (navRef.current && nextElement && !isMenuButton && !containsTarget(navRef, nextElement)) {
      closeAllSubPanels();
      closeMenus();
    }
  };

  const clickOutsideMainNavigationHandler = useCallback(
    (event: MouseEvent) => {
      if (!navRef.current?.contains(event.target as Node)) {
        closeAllSubPanels();

        if (currentOpenMenu === GlobalMenuState.MAIN_NAVIGATION) {
          closeMenus();
        }
      }
    },
    [closeAllSubPanels, closeMenus, currentOpenMenu],
  );

  useEffect(() => {
    document.addEventListener('click', clickOutsideMainNavigationHandler);

    return () => {
      if (clickOutsideMainNavigationHandler) {
        document.removeEventListener('click', clickOutsideMainNavigationHandler);
      }
    };
  }, [clickOutsideMainNavigationHandler, closeAllSubPanels, closeMenus, currentOpenMenu, keysOfOpenPanels]);

  const closeSubPanelsOnEscape = (event: React.KeyboardEvent<HTMLElement>) => {
    if (event.code === KeyEvent.Escape) {
      closeAllSubPanels();
    }
  };

  /**
   * If main navigation is closed via GlobalMenuStore (burger button), close sub panels as well
   */
  useEffect(() => {
    if (currentOpenMenu !== GlobalMenuState.MAIN_NAVIGATION) {
      closeAllSubPanels();
    }
  }, [closeAllSubPanels, currentOpenMenu]);

  /**
   * Triggers axios call to fetch sub panel's data and saves it in navigation state.
   * @panelData: sub panel's data
   * @panelName: name of sub panel
   * @panelLink: link to the page with the navigation of sub panel
   * @panelLevel: current panel number, used to define from which sub panel comes requests
   * @loadingError: to show axios call errors
   * @fetchUrl: used to refetch data, if fetching error occurs
   * @openButtonRef: used to focus correct button, if sub panel closes
   * */
  const fetchSubPanel = (
    fetchUrl: string,
    panelLevel: number,
    name: string,
    url: string,
    nextPanelButtonRef: React.MutableRefObject<HTMLButtonElement | null>,
    loadingError: boolean = false,
  ): void => {
    const newPanel = {
      panelData: undefined,
      panelName: name,
      panelLink: url,
      panelLevel,
      loadingError: false,
      fetchUrl,
      nextPanelButtonRef,
    };
    // if request comes from one panel before, we replace last panel with new one in the list
    if (keysOfOpenPanels.length > 1 && keysOfOpenPanels.length - 1 === panelLevel) {
      // caching: if request comes from same button, it will be skipped
      const panel = mapRef.current.get(fetchUrl);
      if (panel?.panelLevel === panelLevel - 1 && !loadingError) {
        return;
      }
      keysOfOpenPanels.pop();
      keysOfOpenPanels.push(fetchUrl);
    }
    // if request comes from last panel we add new panel at the end of list
    if (keysOfOpenPanels.length === 1 || (keysOfOpenPanels.length > 1 && keysOfOpenPanels.length === panelLevel)) {
      keysOfOpenPanels.push(fetchUrl);
    }
    setKeysOfOpenPanels([...keysOfOpenPanels]);
    // if panel already exist and there was no loadingError, then skip axios call
    const panel = mapRef.current.get(fetchUrl);
    if (panel && !panel.loadingError) {
      return;
    }
    if (!panel || (panel && panel.loadingError)) {
      mapRef.current.set(fetchUrl, newPanel);
    }
    axios
      .get(fetchUrl)
      .then((res) => {
        const oldPanel = mapRef.current.get(fetchUrl);
        if (oldPanel) {
          mapRef.current.set(fetchUrl, { ...oldPanel, panelData: res.data });
          setKeysOfOpenPanels([...keysOfOpenPanels]);
        }
      })
      .catch(() => {
        const oldPanel = mapRef.current.get(fetchUrl);
        if (oldPanel) {
          mapRef.current.set(fetchUrl, { ...oldPanel, loadingError: true });
          setKeysOfOpenPanels([...keysOfOpenPanels]);
        }
      });
  };

  /**
   * There are two cases for back buttons in sub panels:
   * 1) Request comes from last panel: only last panel will be closed.
   * 2) Request comes one panel before: last two panels will be closed
   * */
  const closeSubPanel = (panelLevel: number): void => {
    // if request comes from one panel before we have to remove last two panels of the list
    if (keysOfOpenPanels.length > 2 && keysOfOpenPanels.length - 1 > panelLevel) {
      const newKeysOfOpenPanels = [...keysOfOpenPanels];
      newKeysOfOpenPanels.pop();
      newKeysOfOpenPanels.pop();
      setKeysOfOpenPanels([...newKeysOfOpenPanels]);
    }
    // if request comes from last panel we have to remove only last panel of the list
    if (keysOfOpenPanels.length > 1 && keysOfOpenPanels.length - 1 === panelLevel) {
      const newKeysOfOpenPanels = [...keysOfOpenPanels];
      newKeysOfOpenPanels.pop();
      setKeysOfOpenPanels([...newKeysOfOpenPanels]);
    }
  };

  return {
    navRef,
    subPanelsContainerRef,
    activeOpenButtonId,
    subPanelContainerClass,
    setActiveOpenButtonId,
    closeMainNavigationWhenFocusOut,
    closeAllSubPanels,
    closeSubPanelsOnEscape,
    fetchSubPanel,
    closeSubPanel,
    keysOfOpenPanels,
    mapRef,
  };
}
